import React from 'react';
import { useLocation, useNavigate, useOutlet, useSearchParams } from 'react-router-dom';
import { AnimatePresence } from 'motion/react';
import { SparklesIcon, Squares2X2Icon } from '@heroicons/react/24/outline';

import { Button } from '@oysterjs/ui/button';

import {
  CaraWorkspaceUseCase,
  type CustomFormListFieldsFragment,
  type ExcelSpreadsheetTemplateFieldsFragment,
  type HtmlDocumentTemplateFieldsFragment,
  type CustomPresetFieldsFragment,
  type LoggedInAgencyUserFieldsFragment,
  type WordDocumentTemplateFieldsFragment
} from '~/api/graphql';
import { PermissionNotice } from '~/components/permissions';
import { useDefaultVisibilityValue } from '~/components/visibility/default-visibility';
import { type VisibilitySelectorValue } from '~/components/visibility/visibility-selector.utils';
import { useCreateCaraWorkspace } from '~/components/workspaces/use-create-cara-workspace';
import { useComposeEffortLevel } from '~/components/presets/effort-level';
import { CaraInput } from '~/components/cara-input/cara-input';
import {
  amsContextFromAttachments,
  amsRefsFromAttachments,
  filesFromAttachments,
  idsByKind
} from '~/components/cara-input/cara-attachment';
import { buildCaraFallbackMessage } from '~/components/workspaces/lib/utils';
import { UseCaseRunCard } from './run-card-dialog';
import { UseCaseBrowser } from './use-case-browser';
import { PresetSuggestionFlow } from './preset-suggestion-flow';
import { CustomPresetTile, UseCaseTile } from './use-case-tile';
import { resolveRequestedUseCase } from './use-case-catalog';
import { useHomeLauncher } from './use-home-launcher';
import { pinKey } from '~/api/data/home/use-pinned-home-items';

/**
 * `?useCase=` opens a run card; retired ids still resolve because search rows
 * emit the old vocabulary.
 */
export const CopilotHomeV2 = (props: {
  agencyUser: LoggedInAgencyUserFieldsFragment | null;
  customForms: CustomFormListFieldsFragment[];
  htmlDocumentTemplatesForPicker: HtmlDocumentTemplateFieldsFragment[];
  wordDocumentTemplatesForPicker: WordDocumentTemplateFieldsFragment[];
  excelSpreadsheetTemplatesForPicker: ExcelSpreadsheetTemplateFieldsFragment[];
  customPresets: CustomPresetFieldsFragment[];
}) => {
  const outlet = useOutlet();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultVisibility = useDefaultVisibilityValue();

  const [composeVisibility, setComposeVisibility] =
    React.useState<VisibilitySelectorValue>(defaultVisibility);
  const [browserOpen, setBrowserOpen] = React.useState(false);
  const [suggestingPresets, setSuggestingPresets] = React.useState(false);
  // Keep the use case after close so the dialog still has content while it
  // animates out. Reopening remounts the form, so it starts blank either way.
  const [runCard, setRunCard] = React.useState<{
    id: CaraWorkspaceUseCase;
    deliverableKey?: string;
  } | null>(null);
  const [runCardShowing, setRunCardShowing] = React.useState(false);

  // Both the compose box and every tile on this page start a workspace, so
  // without CREATE the page has nothing to run -- pins stay visible and inert.
  const {
    canCreate,
    hasProAccess,
    runnablePresets,
    customFormOptions,
    htmlTemplateOptions,
    wordTemplateOptions,
    excelTemplateOptions,
    libraries,
    askCaraEnabled,
    pins,
    tiles
  } = useHomeLauncher({
    agencyUser: props.agencyUser,
    customForms: props.customForms,
    htmlDocumentTemplates: props.htmlDocumentTemplatesForPicker,
    wordDocumentTemplates: props.wordDocumentTemplatesForPicker,
    excelSpreadsheetTemplates: props.excelSpreadsheetTemplatesForPicker,
    customPresets: props.customPresets
  });

  const { createWorkspace, error, clearError } = useCreateCaraWorkspace(composeVisibility);
  const composeEffort = useComposeEffortLevel();

  React.useEffect(() => {
    const requested = searchParams.get('useCase');
    if (!requested) {
      return;
    }
    // Consume immediately so closing the card and reloading does not reopen it.
    setSearchParams({}, { replace: true });
    const match = resolveRequestedUseCase(requested.toUpperCase() as CaraWorkspaceUseCase);
    if (match) {
      setRunCard({ id: match.id });
      setRunCardShowing(true);
    }
  }, [searchParams, setSearchParams]);

  const openCustomPreset = (id: string) => {
    setBrowserOpen(false);
    navigate(`/app/home/presets/${id}`);
  };

  const openUseCase = (id: CaraWorkspaceUseCase, deliverableKey?: string) => {
    clearError();
    setBrowserOpen(false);
    setRunCard({ id, deliverableKey });
    setRunCardShowing(true);
  };

  const firstName = props.agencyUser?.name?.trim().split(/\s+/)[0];

  return (
    <div className="py-8">
      <div className="mx-auto w-full max-w-4xl">
        <h1 className="m-0 font-heading text-2xl font-semibold tracking-tight text-neutral-900 dark:text-white">
          {firstName ? `What can Cara do for you, ${firstName}?` : 'What can Cara do for you?'}
        </h1>
        <p className="mt-1.5 text-sm text-neutral-500 dark:text-neutral-400">
          Ask anything, or start from one of your pinned workflows.
        </p>

        {!canCreate && (
          <PermissionNotice className="mt-6">
            You don&rsquo;t have permission to start workspaces, so presets on this page cannot be
            run. Ask an agency admin for access.
          </PermissionNotice>
        )}

        {canCreate && askCaraEnabled && (
          <CaraInput
            error={error}
            draftStorageKey="cara-home-input"
            onAttachmentsChanged={clearError}
            onSubmit={async (message, attachments) => {
              const customFormIds = idsByKind(attachments, 'custom-form');
              const htmlTemplateIds = idsByKind(attachments, 'html-template');
              const wordTemplateIds = idsByKind(attachments, 'word-template');
              const excelTemplateIds = idsByKind(attachments, 'excel-template');
              const text =
                message.trim() ||
                buildCaraFallbackMessage({
                  customFormIds,
                  htmlTemplateIds,
                  wordTemplateIds,
                  excelTemplateIds
                });
              const amsContext = amsContextFromAttachments(attachments);
              const amsRefs = amsRefsFromAttachments(attachments);
              return await createWorkspace(
                text,
                filesFromAttachments(attachments).map((f) => ({ upload: f })),
                {
                  customFormIds,
                  htmlDocumentTemplateIds: htmlTemplateIds,
                  wordDocumentTemplateIds: wordTemplateIds,
                  excelSpreadsheetTemplateIds: excelTemplateIds,
                  visibility: composeVisibility,
                  amsContext: amsContext.length > 0 ? amsContext : undefined,
                  effortLevel: composeEffort.level,
                  amsRefs: amsRefs.length > 0 ? amsRefs : undefined
                }
              );
            }}
            customFormPicker={{ options: customFormOptions }}
            htmlTemplatePicker={
              htmlTemplateOptions.length > 0 ? { options: htmlTemplateOptions } : undefined
            }
            wordTemplatePicker={{ options: wordTemplateOptions }}
            excelTemplatePicker={{ options: excelTemplateOptions }}
            enableAmsDocumentPicker
            visibilityPicker={{ value: composeVisibility, onChange: setComposeVisibility }}
            effortPicker={composeEffort.picker}
          />
        )}

        {canCreate && !pins.locked && tiles.length > 0 && (
          <div className="mt-8 flex items-center justify-end gap-4">
            <Button color="sky" onClick={() => setBrowserOpen(true)}>
              <Squares2X2Icon data-slot="icon" />
              Browse presets
            </Button>
          </div>
        )}

        {tiles.length === 0 ? (
          <div className="mt-8 flex flex-col items-center gap-4 rounded-xl border border-dashed border-neutral-300 dark:border-neutral-600 px-6 py-12 text-center">
            <Squares2X2Icon className="size-12 text-neutral-400 dark:text-neutral-500" />
            <p className="m-0 max-w-xl text-pretty text-sm text-neutral-500 dark:text-neutral-400">
              {pins.locked
                ? 'Your agency has not put any presets on this home screen yet. Ask an agency admin to add some.'
                : 'Nothing pinned to your home page yet. Browse the full catalog and pin what you run, or answer three questions and we will suggest a starting set.'}
            </p>
            {canCreate && !pins.locked && (
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button color="white" onClick={() => setBrowserOpen(true)}>
                  Browse presets
                </Button>
                <Button color="sky" onClick={() => setSuggestingPresets(true)}>
                  <SparklesIcon data-slot="icon" />
                  Suggest presets
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div
            role="group"
            aria-label="My presets"
            className="mt-3.5 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3"
          >
            {tiles.map((tile) =>
              'preset' in tile ? (
                <CustomPresetTile
                  key={pinKey(tile.pin)}
                  preset={tile.preset}
                  disabled={!canCreate}
                  onClick={() => openCustomPreset(tile.preset.id)}
                />
              ) : (
                <UseCaseTile
                  key={pinKey(tile.pin)}
                  useCase={tile.useCase}
                  disabled={!canCreate || (tile.useCase.pro && !hasProAccess)}
                  disabledReason={
                    canCreate ? 'requires the Pro plan' : 'you cannot start workspaces'
                  }
                  onClick={() => openUseCase(tile.useCase.id)}
                />
              )
            )}
          </div>
        )}
      </div>

      {/* One AnimatePresence per overlay, each with a stable key, matching how the
          call, email and preset detail panels are mounted. */}
      <AnimatePresence mode="wait" initial>
        {browserOpen && (
          <UseCaseBrowser
            key="use-case-browser"
            onClose={() => setBrowserOpen(false)}
            pinned={pins.pinned}
            isPinned={pins.isPinned}
            onTogglePin={pins.toggle}
            onRemovePin={pins.remove}
            onReorder={pins.reorder}
            customPresets={runnablePresets}
            libraries={libraries}
            onRun={openUseCase}
            onRunCustomPreset={openCustomPreset}
            hasProAccess={hasProAccess}
          />
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait" initial>
        {suggestingPresets && (
          <PresetSuggestionFlow
            key="preset-suggestions"
            onClose={() => setSuggestingPresets(false)}
            isPinned={pins.isPinned}
            onTogglePin={pins.toggle}
            onPinAll={pins.pinAll}
            onBrowseAll={() => {
              setSuggestingPresets(false);
              setBrowserOpen(true);
            }}
            libraries={libraries}
            hasProAccess={hasProAccess}
          />
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait" initial>
        {runCardShowing && runCard && (
          <UseCaseRunCard
            key="use-case-run-card"
            useCaseId={runCard.id}
            initialDeliverableKey={runCard.deliverableKey}
            onClose={() => setRunCardShowing(false)}
            onChangeUseCase={(next) => setRunCard({ id: next })}
            createWorkspace={createWorkspace}
            error={error}
            hasProAccess={hasProAccess}
            libraries={libraries}
          />
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait" initial>
        {outlet && React.cloneElement(outlet, { key: location.pathname })}
      </AnimatePresence>
    </div>
  );
};
